/**
 * What each module's permission cells actually MEAN. Generic CRUD is meaningless for pages like
 * Transaction Monitoring, so each module declares the functionalities that apply to it, with real
 * labels. Modules not listed fall back to plain CRUD (+ Export) for management-style pages.
 *
 * Keys map 1:1 to the matrix flags (canView/canCreate/canUpdate/canDelete/canExport) the backend
 * stores and enforces — we're only choosing WHICH apply per module and how they're labelled.
 */
export type CapKey = 'canView' | 'canCreate' | 'canUpdate' | 'canDelete' | 'canExport';

export interface Capability {
  key: CapKey;
  label: string;
  hint?: string;
}

export const DEFAULT_LABEL_BY_KEY: Record<CapKey, string> = {
  canView: 'View',
  canCreate: 'Create',
  canUpdate: 'Edit',
  canDelete: 'Delete',
  canExport: 'Export',
};

export const DEFAULT_CAPABILITIES: Capability[] = [
  { key: 'canView', label: 'View' },
  { key: 'canCreate', label: 'Create' },
  { key: 'canUpdate', label: 'Edit' },
  { key: 'canDelete', label: 'Delete' },
  { key: 'canExport', label: 'Export' },
];

/** Optional aliases when API moduleCode differs from our config key. */
const MODULE_CODE_ALIASES: Record<string, string> = {};

export function normalizeModuleCode(moduleCode: string): string {
  const trimmed = String(moduleCode ?? '').trim();
  return MODULE_CODE_ALIASES[trimmed] ?? trimmed;
}

export const MODULE_CAPABILITIES: Record<string, Capability[]> = {
  'TRAN-MON-09': [
    { key: 'canView', label: 'View', hint: 'Open the page and see transactions' },
    { key: 'canExport', label: 'Export', hint: 'Download the transactions report' },
    { key: 'canCreate', label: 'Create Order', hint: 'Create an order / payment link' },
  ],
  PROFILE: [
    { key: 'canView', label: 'View', hint: 'See own profile' },
    { key: 'canUpdate', label: 'Edit', hint: 'Edit own profile' },
  ],
  'MERCH-DEVELOPERS': [
    { key: 'canView', label: 'View', hint: 'Open the Developers page' },
    { key: 'canUpdate', label: 'Manage Keys', hint: 'Generate/update API keys and webhook settings' },
  ],
  'MERCH-DOCS': [
    { key: 'canView', label: 'View', hint: 'Open the Documentation page' },
  ],
  'MERCH-STOREFRONT-URLS': [
    { key: 'canView', label: 'View', hint: 'See submitted storefront URLs' },
    { key: 'canCreate', label: 'Submit URL', hint: 'Submit a new storefront domain for approval' },
  ],
};

export function capabilitiesFor(moduleCode: string): Capability[] {
  const code = normalizeModuleCode(moduleCode);
  return MODULE_CAPABILITIES[code] ?? DEFAULT_CAPABILITIES;
}

export type StandardMatrixColumnId =
  | 'view'
  | 'create'
  | 'edit'
  | 'delete'
  | 'export'
  | 'manageKeys';

export interface MatrixColumnDef {
  id: string;
  header: string;
}

/** Stable id for a non-default capability label (Create Order, Submit URL, …). */
export function customMatrixColumnId(cap: Capability): string {
  return `custom:${cap.key}:${cap.label.replace(/\s+/g, '_')}`;
}

function usesStandardColumn(cap: Capability): StandardMatrixColumnId | null {
  if (cap.key === 'canUpdate' && cap.label === 'Manage Keys') return 'manageKeys';
  if (cap.label !== DEFAULT_LABEL_BY_KEY[cap.key]) return null;
  const map: Partial<Record<CapKey, StandardMatrixColumnId>> = {
    canView: 'view',
    canCreate: 'create',
    canUpdate: 'edit',
    canDelete: 'delete',
    canExport: 'export',
  };
  return map[cap.key] ?? null;
}

/**
 * Matrix columns for the current user: standard CRUD headers plus extra columns for any
 * custom-labelled capability on the loaded modules (e.g. Create Order, Submit URL).
 */
export function buildMatrixColumns(moduleCodes: string[]): MatrixColumnDef[] {
  const uniqueCodes = [...new Set(moduleCodes.map(normalizeModuleCode))];
  const extras: Partial<Record<CapKey, MatrixColumnDef[]>> = {};

  for (const code of uniqueCodes) {
    for (const cap of capabilitiesFor(code)) {
      if (usesStandardColumn(cap)) continue;
      const id = customMatrixColumnId(cap);
      const bucket = extras[cap.key] ?? [];
      if (!bucket.some((c) => c.id === id)) {
        bucket.push({ id, header: cap.label });
        extras[cap.key] = bucket;
      }
    }
  }

  const col = (id: StandardMatrixColumnId, header: string): MatrixColumnDef => ({ id, header });

  return [
    col('view', 'View'),
    ...(extras.canView ?? []),
    col('create', 'Create'),
    ...(extras.canCreate ?? []),
    col('edit', 'Edit'),
    ...(extras.canUpdate ?? []),
    col('delete', 'Delete'),
    ...(extras.canDelete ?? []),
    col('export', 'Export'),
    ...(extras.canExport ?? []),
    col('manageKeys', 'Manage Keys'),
  ];
}

export type MatrixCell = { kind: 'na' } | { kind: 'cap'; key: CapKey; label: string; hint?: string };

export function matrixCellForColumn(moduleCode: string, columnId: string): MatrixCell {
  const caps = capabilitiesFor(moduleCode);

  if (columnId.startsWith('custom:')) {
    const cap = caps.find((c) => customMatrixColumnId(c) === columnId);
    return cap ? { kind: 'cap', key: cap.key, label: cap.label, hint: cap.hint } : { kind: 'na' };
  }

  const standardId = columnId as StandardMatrixColumnId;
  switch (standardId) {
    case 'view':
      return caps.some((c) => c.key === 'canView' && usesStandardColumn(c) === 'view')
        ? pickCap(caps, 'canView', 'view')
        : { kind: 'na' };
    case 'create':
      return caps.some((c) => c.key === 'canCreate' && usesStandardColumn(c) === 'create')
        ? pickCap(caps, 'canCreate', 'create')
        : { kind: 'na' };
    case 'edit':
      return caps.some((c) => c.key === 'canUpdate' && usesStandardColumn(c) === 'edit')
        ? pickCap(caps, 'canUpdate', 'edit')
        : { kind: 'na' };
    case 'delete':
      return caps.some((c) => c.key === 'canDelete' && usesStandardColumn(c) === 'delete')
        ? pickCap(caps, 'canDelete', 'delete')
        : { kind: 'na' };
    case 'export':
      return caps.some((c) => c.key === 'canExport' && usesStandardColumn(c) === 'export')
        ? pickCap(caps, 'canExport', 'export')
        : { kind: 'na' };
    case 'manageKeys': {
      const cap = caps.find((c) => c.key === 'canUpdate' && c.label === 'Manage Keys');
      return cap ? { kind: 'cap', key: cap.key, label: cap.label, hint: cap.hint } : { kind: 'na' };
    }
    default:
      return { kind: 'na' };
  }
}

function pickCap(
  caps: Capability[],
  key: CapKey,
  standardCol: StandardMatrixColumnId,
): MatrixCell {
  const cap = caps.find((c) => c.key === key && usesStandardColumn(c) === standardCol);
  return cap ? { kind: 'cap', key: cap.key, label: cap.label, hint: cap.hint } : { kind: 'na' };
}

/** Distinct capability keys that apply to this module (for row "Allow all"). */
export function applicableCapKeys(moduleCode: string): CapKey[] {
  return [...new Set(capabilitiesFor(moduleCode).map((c) => c.key))];
}

export type CapFlags = Record<CapKey, boolean>;

/** Capability keys that have a checkbox in this module row (not "—" cells). */
export function capKeysForModuleRow(moduleCode: string, columns: MatrixColumnDef[]): CapKey[] {
  const keys: CapKey[] = [];
  for (const col of columns) {
    const cell = matrixCellForColumn(moduleCode, col.id);
    if (cell.kind === 'cap' && !keys.includes(cell.key)) {
      keys.push(cell.key);
    }
  }
  return keys;
}

/** Allow-all checkbox state: matches visible row checkboxes only. */
export function allowAllCheckedState(
  row: CapFlags,
  moduleCode: string,
  columns: MatrixColumnDef[],
): boolean | 'indeterminate' {
  const keys = capKeysForModuleRow(moduleCode, columns);
  if (keys.length === 0) return false;
  const enabledCount = keys.filter((k) => Boolean(row[k])).length;
  if (enabledCount === 0) return false;
  if (enabledCount === keys.length) return true;
  return 'indeterminate';
}
